// Modules tab on Admin › AI Prompts: one row per prompt, so a module that uses
// several prompts has several rows. Add and Edit open the Prompt Editor for
// that one prompt.

// REACT IMPORTS
import React, { useEffect, useState } from "react";

// MSAL
import { useMsal } from "@azure/msal-react";

// MUI COMPONENTS
import { Button, CircularProgress, IconButton, MenuItem, Table, TableBody, TableCell, TableHead, TableRow, TextField } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";

// API, CONSTANTS
import { getAPICall } from "../../config/apiCalls";
import { apiError, fmtDay } from "../ITBudget/itBudgetUtils";

const firstLine = (text) => String(text || "").split("\n").find((l) => l.trim()) || "";

// The model the prompt runs on: its Active version's, else its Draft's.
const modelOf = (p) => {
	if (p.active_version) return p.active_version.model_name;
	if (p.draft_version) return `${p.draft_version.model_name} (Draft only)`;
	return "—";
};

const ModulesTab = ({ canEdit, onOpen }) => {
	const { instance, accounts } = useMsal();
	const [rows, setRows] = useState(null);
	const [module, setModule] = useState("");
	const [error, setError] = useState("");

	useEffect(() => {
		getAPICall(instance, accounts[0], "/api/ai-prompts/prompts")
			.then((res) => setRows(res.data))
			.catch((err) => setError(apiError(err, "Couldn't load the module list.")));
	}, []);

	if (!rows) return <div className="itBudgetEmpty">{error ? <span className="itBudgetError">{error}</span> : <CircularProgress />}</div>;

	const modules = [...new Set(rows.map((r) => r.module))].sort();
	const shown = module ? rows.filter((r) => r.module === module) : rows;

	return (
		<div className="itBudgetCard">
			<div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
				<TextField select size="small" label="Module" value={module} onChange={(e) => setModule(e.target.value)} sx={{ width: 240 }}>
					<MenuItem value="">All modules</MenuItem>
					{modules.map((m) => (
						<MenuItem key={m} value={m}>
							{m}
						</MenuItem>
					))}
				</TextField>
				<span className="itBudgetMuted">
					{shown.length} prompt{shown.length === 1 ? "" : "s"}
				</span>
			</div>
			<Table size="small">
				<TableHead>
					<TableRow>
						<TableCell>Module</TableCell>
						<TableCell>Handle</TableCell>
						<TableCell>Version</TableCell>
						<TableCell>Model</TableCell>
						<TableCell>Summary</TableCell>
						<TableCell>Last Update</TableCell>
						<TableCell />
					</TableRow>
				</TableHead>
				<TableBody>
					{shown.map((p) => (
						<TableRow key={p.id} hover style={{ cursor: "pointer", ...(p.active ? {} : { opacity: 0.55 }) }} onClick={() => onOpen(p.prompt_key)}>
							<TableCell>{p.module}</TableCell>
							<TableCell className="itBudgetMono">{p.prompt_key}</TableCell>
							<TableCell style={{ whiteSpace: "nowrap" }}>
								{p.active_version ? `v${p.active_version.version}` : "—"}
								{p.draft_version && <span className="itBudgetMuted"> · Draft v{p.draft_version.version}</span>}
							</TableCell>
							<TableCell>{modelOf(p)}</TableCell>
							<TableCell style={{ maxWidth: 420, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={p.description || ""}>
								{firstLine(p.description) || <span className="itBudgetMuted">{p.name}</span>}
								{p.active ? "" : " · Inactive"}
							</TableCell>
							<TableCell>{fmtDay(p.last_change)}</TableCell>
							<TableCell align="right">
								<IconButton
									size="small"
									aria-label={`${canEdit ? "Edit" : "View"} ${p.prompt_key}`}
									onClick={(e) => {
										e.stopPropagation();
										onOpen(p.prompt_key);
									}}
								>
									{canEdit ? <EditIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
								</IconButton>
							</TableCell>
						</TableRow>
					))}
					{!shown.length && (
						<TableRow>
							<TableCell colSpan={7} className="itBudgetMuted">
								No prompts yet.
							</TableCell>
						</TableRow>
					)}
				</TableBody>
			</Table>
			{canEdit && (
				<Button startIcon={<AddIcon />} onClick={() => onOpen(null)} style={{ marginTop: 10 }}>
					Add Module Prompt
				</Button>
			)}
		</div>
	);
};

export default ModulesTab;
